/* Webinars — the operational home for webinar campaigns (2026-09).
 *
 * Overview · Campaigns · Registrations and Attendance · Automation and
 * Messaging · Failures and Retry Queue. Global configuration (master
 * switch, providers, global defaults, template mapping, diagnostics)
 * stays in Administration -> Webinar — nothing is duplicated between the
 * two.
 *
 * Visible to anyone holding an existing webinar capability (webinar.view
 * or webinar.config — the same gate every webinar read endpoint uses).
 * Writes additionally need webinar.config, and attendance corrections
 * webinar.attendance.correct; the backend enforces all of it.
 *
 * ?tab=<tab>&campaign=<campaign_id> deep-links into a tab + campaign.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errDetail } from "@/lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EmptyState } from "@/components/common";
import { Video, ShieldAlert } from "lucide-react";
import { useWebinarCaps } from "@/components/webinar/webinarShared";
import { WebinarOverview } from "@/components/webinar/WebinarOverview";
import { WebinarCampaigns } from "@/components/webinar/WebinarCampaigns";
import { WebinarRegistrations } from "@/components/webinar/WebinarRegistrations";
import { WebinarAutomation } from "@/components/webinar/WebinarAutomation";
import { WebinarFailures } from "@/components/webinar/WebinarFailures";

export const WEBINAR_TABS = ["overview", "campaigns", "registrations", "automation", "failures"];

export default function WebinarsPage() {
  const { canView, canConfig } = useWebinarCaps();
  const [params, setParams] = useSearchParams();
  const tab = WEBINAR_TABS.includes(params.get("tab")) ? params.get("tab") : "overview";
  const campaignParam = params.get("campaign") || "";
  const [campaigns, setCampaigns] = useState(null);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [settings, setSettings] = useState(null);
  const [products, setProducts] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const setQuery = useCallback((next) => {
    const p = new URLSearchParams(params);
    Object.entries(next).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
    setParams(p, { replace: true });
  }, [params, setParams]);

  const load = useCallback(async () => {
    if (!canView) return;
    try {
      const [c, s] = await Promise.all([
        api.get("/webinar/campaigns?limit=200"),
        api.get("/webinar/settings"),
      ]);
      setCampaigns(c.data.items);
      setCampaignsTotal(c.data.total);
      setSettings(s.data);
    } catch (e) { toast.error(errDetail(e)); }
  }, [canView]);
  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    if (!canConfig) return;
    // selectable_only mirrors what lead intake will actually accept for a
    // NEW opportunity, so the picker can't offer a product an enrollment
    // would then be rejected for.
    api.get("/products?selectable_only=true").then((r) => setProducts(r.data || []))
      .catch(() => setProducts([]));
  }, [canConfig]);

  // The per-campaign tabs default to the first active campaign, else the
  // newest one — never a silently-empty view when campaigns exist.
  const defaultCampaign = useMemo(() => {
    const list = campaigns || [];
    return (list.find((c) => c.status === "active") || list[0] || {}).campaign_id || "";
  }, [campaigns]);
  const campaignId = campaignParam || defaultCampaign;

  const onChanged = useCallback(() => setRefreshKey((k) => k + 1), []);
  const openCampaign = useCallback((id, nextTab) => setQuery({ campaign: id, tab: nextTab }), [setQuery]);

  if (!canView) {
    return (
      <EmptyState icon={ShieldAlert} title="No webinar access"
                  hint="Webinars are visible to roles holding a webinar capability." testId="webinars-no-access" />
    );
  }

  return (
    <div className="space-y-4" data-testid="webinars-page">
      <div>
        <h1 className="font-heading text-2xl font-semibold tracking-tight flex items-center gap-2">
          <Video className="h-6 w-6" /> Webinars
        </h1>
        <p className="text-sm text-muted-foreground">
          Campaigns, registrations and attendance, automated messaging and the failure queue.
          {settings && !settings.enabled && (
            <span className="ml-1 text-[hsl(var(--warning))]" data-testid="webinars-master-off">
              The webinar master switch is OFF — nothing is enrolled, sent or called until it is turned on in Administration → Webinar.
            </span>
          )}
        </p>
      </div>
      <Tabs value={tab} onValueChange={(v) => setQuery({ tab: v })}>
        <TabsList className="h-auto flex-wrap justify-start gap-0.5">
          <TabsTrigger value="overview" data-testid="webinars-tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="campaigns" data-testid="webinars-tab-campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="registrations" data-testid="webinars-tab-registrations">Registrations &amp; attendance</TabsTrigger>
          <TabsTrigger value="automation" data-testid="webinars-tab-automation">Automation &amp; messaging</TabsTrigger>
          <TabsTrigger value="failures" data-testid="webinars-tab-failures">Failures &amp; retry queue</TabsTrigger>
        </TabsList>
        <TabsContent value="overview">
          {tab === "overview" && (
            <WebinarOverview campaigns={campaigns} campaignsTotal={campaignsTotal}
                             onOpenCampaign={openCampaign} refreshKey={refreshKey} />
          )}
        </TabsContent>
        <TabsContent value="campaigns">
          {tab === "campaigns" && (
            <WebinarCampaigns campaigns={campaigns} campaignsTotal={campaignsTotal} settings={settings}
                              products={products} onChanged={onChanged}
                              selectedId={campaignParam} onSelect={(id) => setQuery({ campaign: id })}
                              onOpenCampaign={openCampaign} />
          )}
        </TabsContent>
        <TabsContent value="registrations">
          {tab === "registrations" && (
            <WebinarRegistrations campaigns={campaigns} campaignId={campaignId}
                                  onCampaignChange={(id) => setQuery({ campaign: id })} refreshKey={refreshKey} />
          )}
        </TabsContent>
        <TabsContent value="automation">
          {tab === "automation" && (
            <WebinarAutomation campaigns={campaigns} campaignId={campaignId}
                               onCampaignChange={(id) => setQuery({ campaign: id })}
                               onChanged={onChanged} refreshKey={refreshKey} />
          )}
        </TabsContent>
        <TabsContent value="failures">
          {tab === "failures" && (
            <WebinarFailures campaigns={campaigns} campaignId={campaignParam}
                             onCampaignChange={(id) => setQuery({ campaign: id })} refreshKey={refreshKey} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
